import React from 'react';
import type { CampaignEditRequest, FieldChange, FieldComment } from '@discovr/types';
import { useCampaignCapabilitiesStore, useCampaignUiStore, invalidateLandedCampaignFieldChange } from '@discovr/features';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from '../../components/ui/sheet';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { ArrowRight, CaretDown, CaretUp, XCircle, CheckCircle, ChatCircleText, PaperPlaneRight } from '../../design-system/icons';
import { buildFieldHistoryMap } from './editRequestDiff';
import { FIELD_LABELS, formatFieldValueFor, formatRelativeTime, formatCommentTimestamp, goToField, isAdminRole, isSelfApprovedByAdmin, describeRequestSubmitters, SHEET_CLOSE_ANIMATION_MS, PRODUCTS_FIELD_PATH } from './editRequestFormat';
import { useFieldReviewDecision } from './useFieldReviewDecision';
import { PhaseSubTabs } from './parts/PhaseSubTabs';
import { RoleBadge } from './RoleBadge';
import { AuthorAvatar } from './campaignDetailsParts';
import { ProductsDiffValue } from './ProductListReadonly';
import { useIsTruncated } from '../../lib/useIsTruncated';
import { TruncationTooltip } from '../../components/common/TruncationTooltip';
import { TruncatedText } from '../../components/common/TruncatedText';

const STATUS_BADGE: Record<FieldChange['status'], string> = {
  pending: 'bg-amber-100 text-amber-700',
  approved: 'bg-emerald-100 text-emerald-700',
  rejected: 'bg-rose-100 text-rose-700',
};

/** Read-only history rows (status icon + old→new + date). The interactive
 *  history browser is CommentThreadPopover's pager, not this accordion. */
function InlineHistoryAccordion({ history, fieldPath, currency }: { history: FieldChange[]; fieldPath: string; currency?: string | null }) {
  const [expanded, setExpanded] = React.useState(false);
  if (history.length === 0) return null;
  const rejectedCount = history.filter((h) => h.status === 'rejected').length;

  return (
    <div className="rounded-md border border-border">
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        className="flex w-full items-center justify-between px-2.5 py-2 text-caption-medium text-muted-foreground"
      >
        <span>
          {history.length} earlier change{history.length === 1 ? '' : 's'}
          {rejectedCount > 0 && ` · ${rejectedCount} rejected`}
        </span>
        {expanded ? <CaretUp size={12} /> : <CaretDown size={12} />}
      </button>
      {expanded && (
        <div className="space-y-1.5 border-t border-border px-2.5 py-2">
          {history.map((h, i) => (
            <div key={`${fieldPath}-hist-${i}`} className="flex items-center gap-2 text-caption-regular">
              {h.status === 'rejected' ? (
                <XCircle size={14} className="shrink-0 text-rose-500" weight="fill" />
              ) : (
                <CheckCircle size={14} className="shrink-0 text-emerald-500" weight="fill" />
              )}
              <span className="text-muted-foreground line-through">{formatFieldValueFor(fieldPath, h.old_value, currency)}</span>
              <span className="text-foreground">→ {formatFieldValueFor(fieldPath, h.new_value, currency)}</span>
              <span className="ml-auto shrink-0 text-muted-foreground">{formatRelativeTime(h.reviewed_at ?? h.applied_at ?? '')}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Inline comment thread for the review sidebar, rendered in the card's own
 *  flow: collapsed previews the latest comment, expanded shows the full thread
 *  (capped height) plus a composer. The Details-page overlay keeps the popover
 *  (CommentThreadPopover) since it has no card flow to inline into. */
function InlineCommentThread({
  requestId,
  fieldPath,
  comments,
  locked,
}: {
  requestId: string;
  fieldPath: string;
  comments: FieldComment[];
  locked: boolean;
}) {
  const [expanded, setExpanded] = React.useState(false);
  const [draft, setDraft] = React.useState('');
  const [posting, setPosting] = React.useState(false);
  const { addFieldComment } = useCampaignCapabilitiesStore();
  const { invalidate } = useFieldReviewDecision(requestId, fieldPath);
  const latest = comments[comments.length - 1];
  const previewText = latest
    ? `${latest.author_name ?? 'Unknown'}: ${latest.text || '(no comment)'} · ${formatCommentTimestamp(latest.created_at)}`
    : '';
  const [previewRef, previewTruncated] = useIsTruncated<HTMLButtonElement>(previewText);

  const handlePost = async () => {
    const text = draft.trim();
    if (!text || !addFieldComment) return;
    setPosting(true);
    try {
      await addFieldComment(requestId, fieldPath, text);
      invalidate();
      setDraft('');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to post comment');
    } finally {
      setPosting(false);
    }
  };

  return (
    <div className="space-y-1.5 border-t border-border pt-2.5">
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        className="inline-flex items-center gap-1 text-caption-medium text-foreground hover:underline"
      >
        <ChatCircleText size={13} />
        {comments.length === 0 ? 'Add a comment' : `${comments.length} comment${comments.length === 1 ? '' : 's'}`}
        {expanded ? <CaretUp size={11} /> : <CaretDown size={11} />}
      </button>

      {/* Kept on the raw TruncationTooltip primitive (not TruncatedText): the
          trigger is a <button> with composite JSX content, and the reveal is a
          composed sender+comment+timestamp string, neither of which the
          single-string TruncatedText models. */}
      {!expanded && latest && (
        <TruncationTooltip isTruncated={previewTruncated} content={previewText}>
          <button
            ref={previewRef}
            type="button"
            onClick={() => setExpanded(true)}
            className="block w-full truncate text-left text-caption-regular text-muted-foreground"
          >
            <span className="font-medium text-foreground">{latest.author_name ?? 'Unknown'}</span>
            {': '}
            {latest.text || <span className="italic">(no comment)</span>}
            {' · '}
            {formatCommentTimestamp(latest.created_at)}
          </button>
        </TruncationTooltip>
      )}

      {expanded && (
        <div className="space-y-2.5">
          {comments.length > 0 && (
            <div className="max-h-60 space-y-2.5 overflow-y-auto rounded-md bg-muted/50 p-2">
              {comments.map((c, i) => (
                <div key={i} className="flex items-start gap-2">
                  <AuthorAvatar name={c.author_name} />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-caption-regular text-muted-foreground">
                      <TruncatedText
                        text={c.author_name ?? 'Unknown'}
                        className="max-w-[150px] font-medium text-foreground"
                      />
                      <RoleBadge role={c.author_role} />
                      <span>· {formatCommentTimestamp(c.created_at)}</span>
                    </div>
                    <p className="text-body-sm-regular text-foreground">{c.text}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
          {locked ? (
            <p className="text-caption-regular text-muted-foreground italic">This change is approved — commenting is closed.</p>
          ) : (
            <div className="relative">
              <Textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="Write a comment…"
                rows={2}
                className="field-sizing-fixed min-h-0 resize-none pr-9"
                autoFocus
              />
              <button
                type="button"
                aria-label="Post comment"
                title="Post comment"
                onClick={handlePost}
                disabled={!draft.trim() || posting}
                className="absolute right-1.5 bottom-1.5 flex size-6 items-center justify-center rounded-full bg-foreground text-background disabled:opacity-30"
              >
                <PaperPlaneRight size={13} weight="bold" />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Products get the rich before/after catalog render; every other field keeps
// the scalar strikethrough -> new-value line.
function ChangeValueDisplay({ fieldPath, oldValue, newValue, currency }: { fieldPath: string; oldValue: unknown; newValue: unknown; currency?: string | null }) {
  if (fieldPath === PRODUCTS_FIELD_PATH) {
    return <ProductsDiffValue oldValue={oldValue} newValue={newValue} />;
  }
  return (
    <div className="rounded-md bg-muted px-2.5 py-2 text-body-sm-regular text-muted-foreground">
      <span className="line-through">{formatFieldValueFor(fieldPath, oldValue, currency)}</span>
      {' → '}
      <span className="font-medium text-foreground">{formatFieldValueFor(fieldPath, newValue, currency)}</span>
    </div>
  );
}

function ChangeRow({
  requestId,
  change,
  submittedByName,
  fieldHistory,
  onGoToField,
  showStatus,
  currency,
}: {
  currency?: string | null;
  requestId: string;
  change: FieldChange;
  submittedByName?: string;
  fieldHistory: FieldChange[];
  onGoToField: (fieldPath: string) => void;
  /** Show the per-card status pill. False under a status filter tab (redundant
   *  there); only the "All" tab needs it to disambiguate mixed statuses. */
  showStatus: boolean;
}) {
  const [rejecting, setRejecting] = React.useState(false);
  const [reason, setReason] = React.useState('');
  // Shared handler with CommentThreadPopover's Approve/Reject controls.
  const { approve, reject, deciding } = useFieldReviewDecision(requestId, change.field_path);

  const label = FIELD_LABELS[change.field_path] ?? change.field_path;
  const firstName = (submittedByName ?? 'the brand').split(' ')[0];

  return (
    <div className="space-y-2.5 rounded-lg border border-border p-3">
      <div className="flex items-center justify-between">
        <span className="text-body-sm-medium text-foreground">{label}</span>
        {showStatus && (
          <span className={`rounded-full px-2 py-0.5 text-caption-medium uppercase ${STATUS_BADGE[change.status]}`}>
            {isSelfApprovedByAdmin(change)
              ? 'Applied by admin'
              : change.status === 'approved'
                ? change.applied_at
                  ? 'Applied'
                  : 'Approved'
                : change.status}
          </span>
        )}
      </div>

      <ChangeValueDisplay fieldPath={change.field_path} oldValue={change.old_value} newValue={change.new_value} currency={currency} />

      {submittedByName && (
        <p className="text-caption-regular text-muted-foreground">
          Changed by <span className="font-medium text-foreground">{submittedByName}</span>
        </p>
      )}

      <InlineHistoryAccordion history={fieldHistory} fieldPath={change.field_path} currency={currency} />

      {change.status === 'pending' && rejecting ? (
        <div className="space-y-2 border-t border-border pt-2.5">
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={`Tell ${firstName} why you're rejecting this (optional) — they'll see this note.`}
            rows={3}
            className="field-sizing-fixed min-h-0 resize-none"
            autoFocus
          />
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setRejecting(false)}>
              Cancel
            </Button>
            <Button
              size="sm"
              variant="destructive"
              disabled={deciding}
              loading={deciding}
              onClick={async () => {
                try {
                  await reject(reason.trim());
                  setRejecting(false);
                } catch {
                  // useFieldReviewDecision already surfaced a toast — keep
                  // the reason box open so the draft isn't lost.
                }
              }}
            >
              Send rejection
            </Button>
          </div>
        </div>
      ) : (
        // Go-to-field (left) + Approve/Reject (right) share one row; non-pending
        // changes keep just the Go-to-field link.
        <div className="flex items-center justify-between gap-2 border-t border-border pt-2.5">
          <button
            type="button"
            onClick={() => onGoToField(change.field_path)}
            className="inline-flex items-center gap-1 text-caption-medium text-foreground hover:underline"
          >
            Go to field <ArrowRight size={12} weight="bold" />
          </button>
          {change.status === 'pending' && (
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={deciding}
                onClick={() => setRejecting(true)}
                className="rounded-full border-rose-500 text-rose-600 hover:bg-rose-50 hover:text-rose-700"
              >
                Reject
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={deciding}
                loading={deciding}
                onClick={() => approve()}
                className="rounded-full border-emerald-500 text-emerald-600 hover:bg-emerald-50 hover:text-emerald-700"
              >
                Approve
              </Button>
            </div>
          )}
        </div>
      )}

      <InlineCommentThread
        requestId={requestId}
        fieldPath={change.field_path}
        comments={change.comments ?? []}
        locked={change.status === 'approved' && !isAdminRole(change.submitted_by_role)}
      />
    </div>
  );
}

interface EditRequestReviewPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editRequest: CampaignEditRequest;
  editHistory: CampaignEditRequest[];
  /** Campaign budget currency, so total_budget diffs read in it. */
  currency?: string | null;
}

type StatusTab = 'all' | 'pending' | 'approved' | 'rejected';

const TAB_LABEL: Record<StatusTab, string> = { all: 'All', pending: 'Pending', approved: 'Approved', rejected: 'Rejected' };
const STATUS_TAB_KEYS: readonly StatusTab[] = ['all', 'pending', 'approved', 'rejected'];

/** Admin-only diff/review panel — approve/reject each pending FieldChange,
 *  then Apply commits the approved-and-unapplied subset atomically. */
export function EditRequestReviewPanel({ open, onOpenChange, editRequest, editHistory, currency }: EditRequestReviewPanelProps) {
  const { reviewEditRequest } = useCampaignCapabilitiesStore();
  const { campaignId } = useCampaignUiStore();
  const queryClient = useQueryClient();
  const [applying, setApplying] = React.useState(false);
  const [tab, setTab] = React.useState<StatusTab>('all');

  // Apply commits the approved subset server-side and refreshes the affected queries.
  const invalidate = () => {
    if (!campaignId) return;
    invalidateLandedCampaignFieldChange(queryClient, campaignId);
  };

  if (!reviewEditRequest) return null;

  const fieldHistoryMap = buildFieldHistoryMap(editHistory, editRequest.id);
  const approvedUnapplied = editRequest.changes.filter((c) => c.status === 'approved' && !c.applied_at).length;

  // Newest-activity-first. A never-touched (still pending) field sorts by its
  // own submitted_at, falling back to epoch on legacy data (so it sorts last,
  // not NaN). Paired with each change's original index in editRequest.changes
  // (the same field_path can appear twice in one request), which is a stable,
  // collision-free row key since MergeChanges never reorders the array.
  const sortedChanges = editRequest.changes
    .map((c, originalIndex) => ({ c, originalIndex }))
    .sort((a, b) => {
      const ta = new Date(a.c.reviewed_at ?? a.c.applied_at ?? a.c.submitted_at ?? 0).getTime();
      const tb = new Date(b.c.reviewed_at ?? b.c.applied_at ?? b.c.submitted_at ?? 0).getTime();
      return tb - ta;
    });

  const visibleChanges = tab === 'all' ? sortedChanges : sortedChanges.filter(({ c }) => c.status === tab);

  // Counts for the PhaseSubTabs pill row.
  const statusTabs = STATUS_TAB_KEYS.map((t) => ({
    key: t,
    label: TAB_LABEL[t],
    count: t === 'all' ? editRequest.changes.length : editRequest.changes.filter((c) => c.status === t).length,
  }));

  const handleGoToField = (fieldPath: string) => {
    onOpenChange(false);
    window.setTimeout(() => goToField(fieldPath), SHEET_CLOSE_ANIMATION_MS);
  };
  const handleApply = async () => {
    setApplying(true);
    try {
      await reviewEditRequest.apply(editRequest.id);
      invalidate();
      toast.success('Changes applied');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to apply changes — the campaign may have just locked.');
    } finally {
      setApplying(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-[440px] sm:max-w-[440px]">
        <SheetHeader>
          <SheetTitle>Changes to review ({editRequest.changes.length})</SheetTitle>
          {editRequest.changes.length > 0 && (
            <p className="text-body-sm-regular text-muted-foreground">
              {describeRequestSubmitters(editRequest.changes)}
              {' · '}
              {formatRelativeTime(editRequest.submitted_at)}
            </p>
          )}
          <PhaseSubTabs active={tab} tabs={statusTabs} onChange={setTab} bare className="pt-2" />
        </SheetHeader>
        <div className="flex-1 space-y-3 overflow-auto px-4 py-2">
          {/* Key on field_path + original array index (see sortedChanges) —
              stable across re-sorts and collision-free on duplicate field_paths. */}
          {visibleChanges.map(({ c, originalIndex }) => (
            <ChangeRow
              key={`${c.field_path}-${originalIndex}`}
              requestId={editRequest.id}
              change={c}
              submittedByName={c.submitted_by_name}
              fieldHistory={fieldHistoryMap.get(c.field_path) ?? []}
              onGoToField={handleGoToField}
              showStatus={tab === 'all'}
              currency={currency}
            />
          ))}
        </div>
        <SheetFooter>
          <Button onClick={handleApply} disabled={approvedUnapplied === 0 || applying} className="w-full">
            {applying ? 'Applying…' : `Apply changes${approvedUnapplied ? ` (${approvedUnapplied})` : ''}`}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
